/**
 * The rules that decide whether a classified page view may be recorded as a touch.
 *
 * A mirror of src/Classification/TouchValidator.php. The client applies these so it can
 * skip a pointless REST call on a page view that could never become a touch; the server
 * applies them again because the client is untrusted input, not a collaborator.
 */

import { CHANNELS, detectClickId, normaliseParams } from './classifier.js';

export const ACCEPTED = '';
export const REJECTED_NO_SIGNAL = 'no_signal';
export const REJECTED_INTERNAL_UTM = 'internal_campaign_leak';

/**
 * BEHAVIOURAL RULE 1: a direct or internal page view must never overwrite last touch.
 *
 * The defect this prevents: visitor clicks an ad, browses four pages, submits a form, and
 * the entry reads direct/(none) because the last page view -- carrying no campaign signal
 * at all -- was recorded as the most recent touch. It is the most common defect in
 * homegrown UTM tracking and it fails without any error.
 *
 * A page view with no UTM, no click ID and no EXTERNAL referrer is not a touch. Not a
 * worse touch, not a lower-priority touch: not a touch, and it never enters the path.
 *
 * @param {object} classification Classification result.
 * @return {boolean} True when the page view may be recorded.
 */
export function isRecordableTouch( classification ) {
	return Boolean( classification ) && classification.channel !== CHANNELS.DIRECT;
}

/**
 * BEHAVIOURAL RULE 4: internal links must never carry campaign parameters.
 *
 * You cannot stop marketing putting UTMs on an internal CTA, but you can refuse to believe
 * them. A touch referred by one of our own hostnames that also carries campaign
 * parameters re-attributes a visitor to a campaign they were already in, overwriting a
 * real acquisition source with an internal click.
 *
 * Click IDs are checked alongside UTM parameters: a click ID surviving on an internal link
 * is the same defect, and it produces a duplicate paid touch once the dedup window passes.
 *
 * @param {{params: object, referrer: string}} signals Page view signal.
 * @param {object}                             rules   Rule accessor.
 * @return {boolean} True when this looks like an internal campaign-parameter leak.
 */
export function isInternalUtmLeak( signals, rules ) {
	let host = '';

	try {
		host = signals.referrer ? new URL( signals.referrer ).hostname.toLowerCase().replace( /\.+$/, '' ) : '';
	} catch {
		return false;
	}

	if ( ! host || ! rules.isInternalHost( host ) ) {
		return false;
	}

	const params = normaliseParams( signals.params );

	for ( const field of [ 'source', 'medium', 'campaign', 'content', 'term' ] ) {
		for ( const name of rules.campaignParams( field ) ) {
			if ( params[ String( name ).toLowerCase() ] ) {
				return true;
			}
		}
	}

	return detectClickId( params, rules ) !== null;
}

/**
 * Applies both rules and returns why, so the caller can raise the right notice.
 *
 * @param {object} classification Classification result.
 * @param {object} signals        Page view signal.
 * @param {object} rules          Rule accessor.
 * @return {string} One of ACCEPTED / REJECTED_NO_SIGNAL / REJECTED_INTERNAL_UTM.
 */
export function verdict( classification, signals, rules ) {
	// The leak check runs first: an internal page view carrying UTMs classifies as a
	// perfectly valid campaign touch, which is exactly why it has to be caught here rather
	// than being allowed out of the classifier looking legitimate.
	if ( isInternalUtmLeak( signals, rules ) ) {
		return REJECTED_INTERNAL_UTM;
	}

	if ( ! isRecordableTouch( classification ) ) {
		return REJECTED_NO_SIGNAL;
	}

	return ACCEPTED;
}
